import { useEffect, useState } from "react";
import {
  supabase,
  setStoredSessionTimeoutMinutes,
} from "@/ajax/clients/supabase";
import OfflineHandler from "@/components/OfflineHandler";
import { OrgLayout } from "@/components/Layouts/OrgLayout";
import { saveLastEmail } from "@/common/utils/lastEmail";
import { z } from "zod";
import { NotFoundComponent } from "@/components/NotFoundComponent";
import {
  createFileRoute,
  redirect,
  Outlet,
  useRouter,
} from "@tanstack/react-router";
import { queryClient } from "@/ajax/queryClient";
import { trpc } from "@/trpc";
import { Toaster } from "@/components/ui/sonner";
import { Storage } from "@/common/utils/storage";
import { TRPCClientError } from "@trpc/client";
import * as Sentry from "@sentry/browser";
import { usePostHog } from "posthog-js/react";
import { getOrgOnboardingSteps } from "@/features/onboarding/utils/getOrgOnboardingSteps";
import { isNative } from "@/common/utils/environment";
import { PushNotificationService } from "@/common/utils/PushNotificationService";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { PushNotificationsPrompt } from "@/components/PushNotifications/PushNotificationsPrompt";
import { NotificationAudience } from "@/components/PushNotifications/notification-audience";
import { enforceMfaGate } from "@/features/mfa/enforceMfaGate";
import { isImpersonating } from "@/features/impersonation/impersonation-session";
import { useTimezoneSync } from "@/features/user/hooks/useTimezoneSync";

const PUSH_PERMISSION_DIALOG_KEY = "org_push_permission_dialog_last_prompt";

export const Route = createFileRoute("/_org")({
  validateSearch: z.object({
    email: z.string().optional(),
    orgId: z.string().optional(),
    orgBusinessUnitId: z.string().optional(),
  }),
  beforeLoad: async ({ location, search }) => {
    if (search.email) {
      saveLastEmail(search.email);
    }

    // Handle ?orgBusinessUnitId= query param (save to storage)
    if (search.orgBusinessUnitId) {
      await Storage.setItem(
        "selectedOrgBusinessUnitId",
        search.orgBusinessUnitId,
      );
    }

    // Handle ?orgId= query param: save to storage and redirect to strip params
    if (search.orgId) {
      await Storage.setItem("selectedOrgId", search.orgId);
      throw redirect({
        to: `${location.pathname}` as string as "/",
        search: (prev) => {
          const { orgId, orgBusinessUnitId, email, ...rest } = prev;
          void orgId;
          void orgBusinessUnitId;
          void email;
          return rest;
        },
        reloadDocument: true,
      });
    }

    const [
      userResponse,
      organizationResponse,
      selectedOrgIdResponse,
      selectedOrgBusinessUnitIdResponse,
    ] = await Promise.allSettled([
      queryClient.fetchQuery(trpc.user.getUser.queryOptions()),
      queryClient.fetchQuery(
        trpc.organization.getUserOrganizations.queryOptions(undefined, {
          // Don't retry auth/MFA denials: a 2FA-required user without a
          // verified second factor gets FORBIDDEN here, and we redirect them to
          // MFA setup below before ever reading this response. Retrying would
          // just add latency.
          retry: (failureCount, error: unknown) => {
            const code = (error as { data?: { code?: string } })?.data?.code;
            if (code === "UNAUTHORIZED" || code === "FORBIDDEN") {
              return false;
            }
            return failureCount < 3;
          },
        }),
      ),
      Storage.getItem("selectedOrgId"),
      Storage.getItem("selectedOrgBusinessUnitId"),
    ]);

    if (userResponse.status === "rejected") {
      if (userResponse.reason instanceof TRPCClientError) {
        if (userResponse.reason.data?.code === "UNAUTHORIZED") {
          throw redirect({
            to: "/auth-org/signin",
            search: {
              redirect: `${location.pathname}${location.searchStr ?? ""}`,
            },
            reloadDocument: true,
          });
        }
      }
      throw new Error("Failed to fetch user");
    }

    // 2FA gate: if this user must use 2FA and the session's second factor
    // isn't verified yet, redirect to MFA setup before touching any org data.
    // The API also enforces this independently.
    await enforceMfaGate(userResponse.value.mfa_required, location.pathname);

    if (organizationResponse.status === "rejected") {
      if (organizationResponse.reason instanceof TRPCClientError) {
        if (organizationResponse.reason.data?.code === "UNAUTHORIZED") {
          throw redirect({
            to: "/auth-org/signin",
            search: {
              redirect: `${location.pathname}${location.searchStr ?? ""}`,
            },
            reloadDocument: true,
          });
        }
      }
      throw new Error("Failed to fetch organizations");
    }

    let selectedOrgId = null;
    let selectedOrgBusinessUnitId = null;

    if (selectedOrgIdResponse.status === "fulfilled") {
      selectedOrgId = selectedOrgIdResponse.value;
    }

    if (selectedOrgBusinessUnitIdResponse.status === "fulfilled") {
      selectedOrgBusinessUnitId = selectedOrgBusinessUnitIdResponse.value;
    }

    let organizations = null;
    if (organizationResponse.status === "fulfilled") {
      organizations = organizationResponse.value;
    }

    let user = null;
    if (userResponse.status === "fulfilled") {
      user = userResponse.value;
    }

    const businessUnitPathMatch =
      location.pathname.match(/^\/org\/bu\/([^/]+)/);
    const businessUnitIdFromPath = businessUnitPathMatch?.[1];
    if (businessUnitIdFromPath) {
      const orgForPathBusinessUnit = organizations?.find((org) =>
        org.businessUnits?.some((bu) => bu.id === businessUnitIdFromPath),
      );

      if (orgForPathBusinessUnit) {
        selectedOrgId = orgForPathBusinessUnit.id;
        selectedOrgBusinessUnitId = businessUnitIdFromPath;
        await Promise.all([
          Storage.setItem("selectedOrgId", selectedOrgId),
          Storage.setItem(
            "selectedOrgBusinessUnitId",
            selectedOrgBusinessUnitId,
          ),
        ]);
      }
    }

    // Check if user needs to complete onboarding (only if they have an org)
    if (user) {
      const isOnboarding =
        (organizations?.length ?? 0) > 0 &&
        getOrgOnboardingSteps(user).length > 0;
      const allowedOnboardingPaths = [
        "/org-onboarding",
        "/org/providers/all",
        "/org/providers/shared",
        "/org/providers",
        "/org/create",
      ];
      const isAllowedPath =
        allowedOnboardingPaths.includes(location.pathname) ||
        location.pathname.startsWith("/org/provider-profile/") ||
        /^\/org\/bu\/[^/]+\/provider-profile\//.test(location.pathname) ||
        /^\/org\/bu\/[0-9a-f-]+\/providers/.test(location.pathname) ||
        /^\/org\/[0-9a-f-]+\/providers/.test(location.pathname);

      if (isOnboarding && !isAllowedPath) {
        throw redirect({
          to: "/org-onboarding",
        });
      }
    }

    if (
      organizations?.length === 0 &&
      ![
        "/org/providers/all",
        "/org/providers/shared",
        "/org/providers",
        "/org/create",
        "/org/home",
      ].includes(location.pathname) &&
      !location.pathname.startsWith("/org/provider-profile/") &&
      !/^\/org\/bu\/[^/]+\/provider-profile\//.test(location.pathname) &&
      !/^\/org\/bu\/[0-9a-f-]+\/providers/.test(location.pathname) &&
      !/^\/org\/[0-9a-f-]+\/providers/.test(location.pathname)
    ) {
      throw redirect({
        to: "/org/providers/shared",
      });
    }

    const savedOrg =
      organizations?.find((org) => org.id === selectedOrgId) ||
      organizations?.[0];
    const savedOrgBusinessUnit =
      savedOrg?.businessUnits?.find(
        (bu) => bu.id === selectedOrgBusinessUnitId,
      ) ?? savedOrg?.businessUnits?.[0];

    return {
      organizations,
      savedOrg,
      savedOrgBusinessUnit,
      user,
    };
  },
  notFoundComponent: () => <NotFoundComponent homePath="/org/home" />,
  component: OrgRouteComponent,
});

function OrgRouteComponent() {
  const router = useRouter();
  const posthog = usePostHog();
  const { user, organizations, savedOrg, savedOrgBusinessUnit } =
    Route.useRouteContext();
  const [showPushDialog, setShowPushDialog] = useState(false);
  useTimezoneSync(user != null);

  useEffect(() => {
    supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_OUT") {
        queryClient.clear();
        router.navigate({
          to: "/auth-org/signin",
          search: {
            redirect: `${router.state.location.pathname}${router.state.location.searchStr ?? ""}`,
          },
          reloadDocument: true,
        });
      }
    });
  }, [router]);

  useEffect(() => {
    // While impersonating, analytics, error reports, and the idle timeout
    // stay tied to the staff member's own sign-in.
    if (isImpersonating()) {
      return;
    }
    if (user) {
      Sentry.setUser({
        id: user?.id,
      });
      posthog.identify(user?.id);
      void setStoredSessionTimeoutMinutes(user.session_timeout_minutes ?? null);
    } else {
      Sentry.setUser(null);
      posthog.reset();
    }
  }, [posthog, user]);

  useEffect(() => {
    if (!user || !isNative()) {
      return;
    }
    const hasCompletedOnboarding = getOrgOnboardingSteps(user).length === 0;
    if (!hasCompletedOnboarding) {
      return;
    }
    const checkPermission = async () => {
      await PushNotificationService.checkPermissionOncePerDay();
      const status = await PushNotificationService.checkPermissionStatus();
      if (status?.receive !== "granted") {
        const today = new Date().toISOString().split("T")[0];
        const lastPrompt = await Storage.getItem(PUSH_PERMISSION_DIALOG_KEY);
        if (lastPrompt !== today) {
          setShowPushDialog(true);
          await Storage.setItem(PUSH_PERMISSION_DIALOG_KEY, today);
        }
      } else {
        await PushNotificationService.registerDeviceToken();
        setShowPushDialog(false);
      }
    };
    checkPermission();
    const interval = setInterval(
      () => {
        void checkPermission();
      },
      24 * 60 * 60 * 1000,
    );
    return () => clearInterval(interval);
  }, [user]);

  const handlePushDialogComplete = async (success: boolean) => {
    setShowPushDialog(false);
    if (success) {
      await PushNotificationService.checkPermissionOncePerDay();
    } else {
      const today = new Date().toISOString().split("T")[0];
      await Storage.setItem(PUSH_PERMISSION_DIALOG_KEY, today);
    }
  };

  const handleDialogOpenChange = (open: boolean) => {
    setShowPushDialog(open);
    if (!open) {
      const today = new Date().toISOString().split("T")[0];
      void Storage.setItem(PUSH_PERMISSION_DIALOG_KEY, today);
    }
  };

  if (!user || !organizations) {
    return <OfflineHandler homePath="/org/home" />;
  }

  const isOnboarding =
    organizations.length > 0 && getOrgOnboardingSteps(user).length > 0;

  return (
    <OrgLayout
      user={user}
      organizations={organizations}
      savedOrg={savedOrg}
      savedOrgBusinessUnit={savedOrgBusinessUnit}
      showSidebar={!isOnboarding}
    >
      <Outlet />
      <Toaster />
      <Dialog open={showPushDialog} onOpenChange={handleDialogOpenChange}>
        <DialogContent className="max-w-xl">
          <DialogTitle className="sr-only">Stay in the loop</DialogTitle>
          <PushNotificationsPrompt
            onComplete={handlePushDialogComplete}
            showHeading
            title="Stay in the loop"
            audience={NotificationAudience.Organization}
          />
        </DialogContent>
      </Dialog>
    </OrgLayout>
  );
}
